/**
 * Raise a quotation (`/leads/:leadId/quote`) — the unprompted path, and the
 * majority one: nobody asked, staff are quoting the buyer's shortlist. The
 * other door is `$leadId.accept.$requestId.tsx`, which answers a specific ask.
 *
 * This URL used to PRINT a priced document straight from the lead's booth
 * lines: no ref, no link, no stored terms, and no record that it was ever sent.
 * The office sent those to buyers after a show, so a buyer could end up holding
 * two priced papers from us with no way to tell which superseded which. It now
 * raises a real `Quotation` — numbered, revisable, with terms, acceptable in
 * one tap — and `/q/:token` is where it prints.
 *
 * `POST /api/leads/:leadId/quotations` has existed since the quotation work
 * landed and nothing called it; the shared schema calls it "the WhatsApp path,
 * and the majority one". This screen is what finally drives it.
 *
 * Lines can be repriced, requantified and removed, but NOT added: the Selection
 * builder already picks products well, and duplicating its catalogue sheet here
 * would make a third picking surface. Reconsider only if the office finds
 * itself bouncing between the two.
 */
import {
  CreateQuotationInput,
  DEFAULT_QUOTATION_TERMS,
  formatMoney,
  NO_AMOUNT_LABEL,
  selectionDocRef,
  type CreatedQuotation
} from '@paradise/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { useLeadDetailQuery } from '@/components/lead-detail/use-lead-detail';
import { QuotationCreatedPanel } from '@/components/quotation/quotation-created-panel';
import { QuotationLineEditor } from '@/components/quotation/quotation-line-editor';
import {
  allLinesPriced,
  linesTotal,
  toQuotationItems,
  type QuotationLine
} from '@/components/quotation/quotation-lines';
import { liveQuotationRefByProduct, useLeadQuotations } from '@/components/quotation/use-lead-quotations';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { apiRequest, describeApiFailure } from '@/lib/api/http';
import { useSessionStore } from '@/lib/auth/session';
import { useCatalogueProducts } from '@/lib/catalogue-data';

export const Route = createFileRoute('/_app/leads/$leadId/quote')({
  staticData: { hideShellChrome: true },
  // `?supersedes=<id>` turns this screen into "revise": lines seed from that
  // quotation instead of the shortlist, and the server revokes it as the new
  // revision is created.
  validateSearch: z.object({ supersedes: z.string().optional() }),
  component: RaiseQuotationPage
});

function RaiseQuotationPage() {
  const { leadId } = Route.useParams();
  const { supersedes } = Route.useSearch();
  const detailQuery = useLeadDetailQuery(leadId);
  const quotationsQuery = useLeadQuotations(leadId);
  const catalogueProducts = useCatalogueProducts();
  const senderName = useSessionStore(state => state.user?.name ?? null);
  const productById = useMemo(() => new Map(catalogueProducts.map(p => [p.id, p] as const)), [catalogueProducts]);

  const [lines, setLines] = useState<QuotationLine[] | null>(null);
  const [terms, setTerms] = useState(DEFAULT_QUOTATION_TERMS);
  const [created, setCreated] = useState<CreatedQuotation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmCreate, setConfirmCreate] = useState(false);

  const detail = detailQuery.data?.detail;

  const revised = quotationsQuery.data?.find(row => row.id === supersedes) ?? null;

  // This screen seeds the WHOLE shortlist, and a shortlist never shrinks — so
  // for a returning buyer it offers goods that are already on a live quotation,
  // and re-quoting them at a new price is how somebody ends up holding two.
  // Display only; staff decide. The quotation being revised is excluded, since
  // replacing it is the entire point of being here.
  const quotedRefByProduct = useMemo(
    () => liveQuotationRefByProduct((quotationsQuery.data ?? []).filter(row => row.id !== supersedes)),
    [quotationsQuery.data, supersedes]
  );
  // Revising has to wait for the old quotation, or the effect below would seed
  // from the shortlist and quietly discard what was actually offered.
  const awaitingRevised = supersedes !== undefined && quotationsQuery.isPending;

  // Seeded from the lead's whole SHORTLIST — booth lines and whatever the buyer
  // ticked on a catalogue link, which since Phase 2 are one list. Prices come
  // from the row, not from a fresh catalogue read (unlike the Selection
  // builder): a quotation is the figure we commit to, and the number someone
  // said out loud at the stand, or showed the buyer on the catalogue, is the
  // closest thing to it we hold. Staff can still change every line.
  //
  // Revising seeds from the quotation being replaced instead: a revision is a
  // change to what we already offered, not a fresh quote off the shortlist.
  useEffect(() => {
    if (detail === undefined || lines !== null || productById.size === 0 || awaitingRevised) return;
    if (revised !== null) {
      setTerms(revised.terms ?? DEFAULT_QUOTATION_TERMS);
      setLines(
        revised.items.map(item => ({
          productId: item.productId,
          productName: item.productNameSnapshot,
          itemNo: productById.get(item.productId)?.itemNo ?? 0,
          quantity: item.quantity,
          quantityDraft: null,
          listPriceSnapshot: item.listPriceSnapshot,
          unitPriceText: item.unitPrice,
          noteText: item.note ?? '',
          priceSource: 'previous'
        }))
      );
      return;
    }
    // The DERIVED shortlist, not `items` — the meeting picks unioned with what
    // the buyer ticked on a catalogue link. Seeding from `items` alone is what
    // made "Raise a quotation" price the wrong 2 of 12.
    setLines(
      detail.shortlist.map(line => {
        const product = productById.get(line.productId);
        return {
          productId: line.productId,
          productName: line.productName,
          itemNo: product?.itemNo ?? 0,
          quantity: line.quantity,
          quantityDraft: null,
          listPriceSnapshot: line.listPriceSnapshot,
          // A line off a band/no-price catalogue has no figure at all, so it
          // starts blank and `allLinesPriced` holds Create shut until someone
          // types one.
          unitPriceText: line.unitPrice ?? '',
          noteText: line.note ?? '',
          priceSource: line.unitPrice === null ? 'none' : line.source === 'REQUEST' ? 'selection' : 'booth'
        };
      })
    );
  }, [detail, lines, productById, revised, awaitingRevised]);

  function patchLine(index: number, patch: Partial<QuotationLine>): void {
    setLines(prev => (prev === null ? prev : prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))));
  }

  async function submit() {
    if (lines === null || detail === undefined) return;
    setBusy(true);
    setError(null);
    // The recipient is snapshotted onto the quotation because there is no
    // QuoteRequest to take it from — this is the path where nobody asked.
    const parsed = CreateQuotationInput.safeParse({
      recipientName: detail.contactName?.trim() ?? detail.company,
      recipientCompany: detail.company,
      recipientEmail: detail.email ?? null,
      recipientPhone: detail.phone ?? null,
      terms: terms.trim() === '' ? null : terms.trim(),
      items: toQuotationItems(lines)
    });
    if (!parsed.success) {
      setError('Every line needs a price before this can be sent.');
      setBusy(false);
      return;
    }
    const path =
      supersedes === undefined
        ? `/api/leads/${leadId}/quotations`
        : `/api/leads/${leadId}/quotations?supersedes=${encodeURIComponent(supersedes)}`;
    const outcome = await apiRequest<CreatedQuotation>(path, { method: 'POST', body: parsed.data });
    setBusy(false);
    if (outcome.kind === 'ok') setCreated(outcome.data);
    else setError(describeApiFailure(outcome));
  }

  if (detailQuery.isPending || awaitingRevised) {
    return (
      <div className="grid h-dvh place-items-center text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <Spinner className="size-4" aria-hidden="true" /> Loading…
        </span>
      </div>
    );
  }

  if (detail === undefined || lines === null) {
    return (
      <div className="grid h-dvh place-items-center p-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-muted-foreground">This buyer could not be loaded.</p>
          <Button render={<Link to="/leads/$leadId" params={{ leadId }} />} variant="outline" size="sm">
            ‹ Back to buyer
          </Button>
        </div>
      </div>
    );
  }

  if (created) {
    return (
      <QuotationCreatedPanel
        created={created}
        leadId={leadId}
        senderName={senderName}
        recipientEmail={detail.email ?? null}
        recipientCountry={detail.country ?? null}
      />
    );
  }

  const total = linesTotal(lines);
  const pendingRequest = detail.quoteRequests.find(entry => entry.status === 'PENDING');

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 p-4 pb-24">
      <header className="flex items-center gap-2.5">
        <Button render={<Link to="/leads/$leadId" params={{ leadId }} />} variant="ghost" size="sm" className="-ml-1.5">
          ‹ Back
        </Button>
        <div className="font-heading truncate text-lg font-semibold">
          {revised === null ? 'Raise a quotation' : `Revise ${selectionDocRef(revised.id)}`}
        </div>
      </header>

      <div className="rounded-lg border bg-card p-3 text-sm">
        <div className="font-medium">{detail.company}</div>
        <div className="text-xs text-muted-foreground">
          {[detail.contactName, detail.email, detail.phone].filter(Boolean).join(' · ') || 'No contact details'}
        </div>
      </div>

      {revised !== null && (
        <p className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
          This replaces <span className="font-medium">{selectionDocRef(revised.id)}</span> as revision{' '}
          <span className="font-medium">{revised.revision + 1}</span>. Sending it kills the old link, so the buyer
          cannot accept the old price.
        </p>
      )}

      {/*
        The wrong-turn guard. This screen prices the WHOLE shortlist; the buyer
        asked about a specific subset of it, and answering with everything reads
        as ignoring the question — and, on a repeat ask, re-quotes goods they
        already ordered. Not a block, since quoting the full list is sometimes
        exactly right, but it must not be silent.
      */}
      {revised === null && pendingRequest !== undefined && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
          <p>
            <span className="font-medium">{pendingRequest.requesterName}</span> asked for a price on{' '}
            {pendingRequest.items.length} {pendingRequest.items.length === 1 ? 'piece' : 'pieces'} and has not been
            answered. This screen prices the buyer's whole shortlist instead.
          </p>
          <Button
            render={<Link to="/leads/$leadId/accept/$requestId" params={{ leadId, requestId: pendingRequest.id }} />}
            variant="outline"
            size="sm"
            className="mt-2"
          >
            Price what they asked for
          </Button>
        </div>
      )}

      {lines.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Nothing to quote yet</EmptyTitle>
            <EmptyDescription>
              This buyer's shortlist is empty. Add what they are interested in on their screen, or send them a catalogue
              and let them pick.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          <div className="flex flex-col gap-2.5">
            {lines.map((line, index) => (
              <QuotationLineEditor
                key={line.productId}
                line={line}
                product={productById.get(line.productId)}
                alreadyQuotedRef={quotedRefByProduct.get(line.productId)}
                onChange={patch => patchLine(index, patch)}
                onRemove={() => setLines(prev => (prev === null ? prev : prev.filter((_, i) => i !== index)))}
              />
            ))}
          </div>

          <div className="flex items-baseline justify-between border-t pt-2.5">
            <span className="text-sm font-medium">Quotation total</span>
            <span className="font-heading text-lg font-semibold">
              {total === null ? NO_AMOUNT_LABEL : formatMoney(total, detail.currency)}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="quotation-terms" className="text-xs font-normal text-muted-foreground">
              Terms printed under the total
            </Label>
            <Textarea
              id="quotation-terms"
              rows={2}
              value={terms}
              onChange={event => setTerms(event.target.value)}
              placeholder="Leave blank to print no terms"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end">
            {/* A pause, not a gate. Creating one mints a live link the buyer can
                accept from — the only action on this screen that leaves the
                building, and it was one tap. */}
            <Button type="button" disabled={busy || !allLinesPriced(lines)} onClick={() => setConfirmCreate(true)}>
              {busy ? 'Creating…' : 'Create quotation'}
            </Button>
          </div>

          <ConfirmDialog
            open={confirmCreate}
            onOpenChange={setConfirmCreate}
            title="Create this quotation?"
            description={`${String(lines.length)} item${lines.length === 1 ? '' : 's'} totalling ${
              total === null ? NO_AMOUNT_LABEL : formatMoney(total, detail.currency)
            } for ${detail.company}. This mints a link the buyer can open and accept from.`}
            confirmLabel="Create quotation"
            onConfirm={() => {
              setConfirmCreate(false);
              void submit();
            }}
          />
        </>
      )}
    </div>
  );
}
